import hyphenEnUs from 'hyphen/en-us';

/** U+00AD — invisible until the browser breaks the line, then a hyphen. */
export const SOFT_HYPHEN = '­';

/**
 * Browsers hyphenate `hyphens: auto` text from the operating system's
 * dictionaries. macOS ships those for 20 languages — German and French among
 * them, but not English. Without a dictionary `hyphens: auto` does nothing and
 * `word-break` chops the word at an arbitrary character, with no hyphen at all
 * ("Administratio | n"). Supplying soft hyphens ourselves restores the missing
 * break points for English while leaving the languages the browser handles
 * correctly untouched. See #1735.
 */
const needsSoftHyphens = (locale?: string): boolean =>
  typeof locale === 'string' && locale.toLowerCase().startsWith('en');

/**
 * Adds soft hyphens to English text so it breaks like German and French do.
 * @param text - The text to hyphenate
 * @param locale - The content language, e.g. `de`, `en` or `en-GB`
 * @returns The text with soft hyphens for English, unchanged otherwise
 * @example
 * hyphenate('Administration', 'en'); // 'Ad­min­is­tra­tion'
 * hyphenate('Hochschule', 'de');     // 'Hochschule' — the browser handles it
 */
const hyphenate = (text: string, locale: string): string => {
  if (!text || !needsSoftHyphens(locale)) {
    return text;
  }
  // Soft hyphens set by the editors express an intent — never second-guess it.
  if (text.includes(SOFT_HYPHEN)) {
    return text;
  }
  return hyphenEnUs.hyphenateSync(text);
};

export default hyphenate;
